/**
 * SYNC Team Selector — Shopify Product Page Component
 * 
 * Drop this file into your theme's Assets folder as: sync-team-selector.js
 * Then reference it in your product template with:
 *   <script src="{{ 'sync-team-selector.js' | asset_url }}" defer></script>
 * And add this placeholder div wherever you want it to appear on the product page:
 *   <div id="sync-team-selector"></div>
 */

(function () {
  const TEAMS_API = "https://syncteamstorebuilder.netlify.app/.netlify/functions/get-teams";
  const MOUNT_ID = "sync-team-selector";

  // ── Styles ─────────────────────────────────────────────────────────────────
  const CSS = `
    #sync-team-selector {
      margin: 16px 0;
      font-family: inherit;
    }
    .sts-label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      margin-bottom: 8px;
      color: #1a1c1f;
    }
    .sts-label span {
      font-weight: 400;
      text-transform: none;
      font-size: 12px;
      color: #6b7280;
      letter-spacing: 0;
    }
    .sts-search-wrap {
      position: relative;
    }
    .sts-search {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #e2e4e7;
      border-radius: 6px;
      font-size: 14px;
      font-family: inherit;
      color: #1a1c1f;
      background: #fff;
      outline: none;
      transition: border-color 0.15s;
      box-sizing: border-box;
    }
    .sts-search:focus {
      border-color: #00AEEF;
      box-shadow: 0 0 0 3px rgba(0,174,239,0.12);
    }
    .sts-search-icon { display: none; }
    .sts-search-icon-hidden {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #9ca3af;
      pointer-events: none;
    }
    .sts-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      background: #fff;
      border: 1px solid #e2e4e7;
      border-radius: 6px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.10);
      max-height: 260px;
      overflow-y: auto;
      z-index: 999;
      display: none;
    }
    .sts-dropdown.open {
      display: block;
    }
    .sts-option {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 12px;
      cursor: pointer;
      font-size: 14px;
      color: #1a1c1f;
      transition: background 0.1s;
    }
    .sts-option:hover,
    .sts-option.focused {
      background: #f4f5f6;
    }
    .sts-option.selected {
      background: #e8f7fd;
      color: #0077a8;
      font-weight: 500;
    }
    .sts-logo {
      width: 48px;
      height: 48px;
      object-fit: contain;
      border-radius: 3px;
      flex-shrink: 0;
      background: #f4f5f6;
    }
    .sts-logo-placeholder {
      width: 48px;
      height: 48px;
      border-radius: 3px;
      background: #e2e4e7;
      flex-shrink: 0;
    }
    .sts-no-results {
      padding: 12px;
      font-size: 13px;
      color: #6b7280;
      text-align: center;
    }
    .sts-selected-display {
      display: none;
      align-items: center;
      gap: 8px;
      margin-top: 8px;
      padding: 8px 10px;
      background: #e8f7fd;
      border: 1px solid #00AEEF;
      border-radius: 6px;
      font-size: 13px;
      color: #0077a8;
      font-weight: 500;
    }
    .sts-selected-display.visible {
      display: flex;
    }
    .sts-clear {
      margin-left: auto;
      background: none;
      border: none;
      cursor: pointer;
      color: #9ca3af;
      font-size: 16px;
      line-height: 1;
      padding: 0 2px;
    }
    .sts-clear:hover { color: #1a1c1f; }
    .sts-loading {
      padding: 12px;
      font-size: 13px;
      color: #6b7280;
      text-align: center;
    }
    /* Hidden line item property input — submits with the form */
    .sts-hidden-input { display: none; }
  `;

  // ── State ──────────────────────────────────────────────────────────────────
  let allTeams = [];
  let selectedTeam = null;
  let focusedIndex = -1;
  let dropdownOpen = false;

  // ── DOM refs ───────────────────────────────────────────────────────────────
  let searchEl, dropdownEl, selectedDisplayEl, hiddenInputEl;

  // ── Inject styles ──────────────────────────────────────────────────────────
  function injectStyles() {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // ── Render mount point ─────────────────────────────────────────────────────
  function renderShell(mount) {
    mount.innerHTML = `
      <label class="sts-label">
        Select Your Team <span>— if adding logo</span>
      </label>
      <div class="sts-search-wrap">
        <svg class="sts-search-icon" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <input
          class="sts-search"
          type="text"
          placeholder="Search for your team..."
          autocomplete="off"
          aria-label="Search for your team"
          aria-autocomplete="list"
          aria-expanded="false"
        />
        <div class="sts-dropdown" role="listbox"></div>
      </div>
      <div class="sts-selected-display">
        <span class="sts-selected-name"></span>
        <button class="sts-clear" type="button" aria-label="Clear selection">×</button>
      </div>
      <!-- Line item property: submitted with add-to-cart form -->
      <input class="sts-hidden-input" type="text" name="properties[Team]" value="" />
    `;

    searchEl = mount.querySelector(".sts-search");
    dropdownEl = mount.querySelector(".sts-dropdown");
    selectedDisplayEl = mount.querySelector(".sts-selected-display");
    hiddenInputEl = mount.querySelector(".sts-hidden-input");

    bindEvents();
  }

  // ── Render dropdown options ────────────────────────────────────────────────
  function renderOptions(teams) {
    if (teams.length === 0) {
      dropdownEl.innerHTML = `<div class="sts-no-results">No teams found</div>`;
      return;
    }

    dropdownEl.innerHTML = teams
      .map((team, i) => {
        const logo = team.logo
          ? `<img class="sts-logo" src="${team.logo}" alt="" loading="lazy" />`
          : `<div class="sts-logo-placeholder"></div>`;
        const isSelected = selectedTeam && selectedTeam.handle === team.handle;
        return `
          <div
            class="sts-option${isSelected ? " selected" : ""}"
            role="option"
            aria-selected="${isSelected}"
            data-index="${i}"
            data-handle="${team.handle}"
            data-name="${team.name}"
          >
            ${logo}
            <span>${team.name}</span>
          </div>
        `;
      })
      .join("");

    // Click handler for each option
    dropdownEl.querySelectorAll(".sts-option").forEach((el) => {
      el.addEventListener("mousedown", (e) => {
        e.preventDefault(); // prevent blur before click
        selectTeam({ handle: el.dataset.handle, name: el.dataset.name });
      });
    });
  }

  // ── Filter teams by search query ───────────────────────────────────────────
  function filterTeams(query) {
    const q = query.trim().toLowerCase();
    if (!q) return allTeams;
    return allTeams.filter((t) => t.name.toLowerCase().includes(q));
  }

  // ── Write value to Infinite Options Team field ────────────────────────────
  function setIOTeamField(value) {
    const ioField = document.querySelector(".io-team-hidden input, input.io-team-hidden");
    if (ioField) {
      ioField.value = value;
      ioField.dispatchEvent(new Event("change", { bubbles: true }));
      ioField.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  // ── Select a team ──────────────────────────────────────────────────────────
  function selectTeam(team) {
    selectedTeam = team;
    hiddenInputEl.value = team.name;
    setIOTeamField(team.name);
    searchEl.value = "";
    searchEl.setAttribute("aria-expanded", "false");
    closeDropdown();

    selectedDisplayEl.querySelector(".sts-selected-name").textContent = team.name;
    selectedDisplayEl.classList.add("visible");
  }

  // ── Clear selection ────────────────────────────────────────────────────────
  function clearSelection() {
    selectedTeam = null;
    hiddenInputEl.value = "";
    setIOTeamField("");
    selectedDisplayEl.classList.remove("visible");
    searchEl.value = "";
    searchEl.focus();
  }

  // ── Open / close dropdown ──────────────────────────────────────────────────
  function openDropdown() {
    dropdownOpen = true;
    dropdownEl.classList.add("open");
    searchEl.setAttribute("aria-expanded", "true");
    focusedIndex = -1;
    renderOptions(filterTeams(searchEl.value));
  }

  function closeDropdown() {
    dropdownOpen = false;
    dropdownEl.classList.remove("open");
    searchEl.setAttribute("aria-expanded", "false");
    focusedIndex = -1;
  }

  // ── Keyboard navigation ────────────────────────────────────────────────────
  function moveFocus(dir) {
    const options = dropdownEl.querySelectorAll(".sts-option");
    if (!options.length) return;
    options.forEach((o) => o.classList.remove("focused"));
    focusedIndex = Math.max(0, Math.min(options.length - 1, focusedIndex + dir));
    options[focusedIndex].classList.add("focused");
    options[focusedIndex].scrollIntoView({ block: "nearest" });
  }

  // ── Event bindings ─────────────────────────────────────────────────────────
  function bindEvents() {
    searchEl.addEventListener("focus", openDropdown);
    searchEl.addEventListener("blur", () => setTimeout(closeDropdown, 150));

    searchEl.addEventListener("input", () => {
      renderOptions(filterTeams(searchEl.value));
      if (!dropdownOpen) openDropdown();
    });

    searchEl.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown") { e.preventDefault(); moveFocus(1); }
      if (e.key === "ArrowUp") { e.preventDefault(); moveFocus(-1); }
      if (e.key === "Enter") {
        e.preventDefault();
        const focused = dropdownEl.querySelector(".sts-option.focused");
        if (focused) selectTeam({ handle: focused.dataset.handle, name: focused.dataset.name });
      }
      if (e.key === "Escape") closeDropdown();
    });

    selectedDisplayEl.querySelector(".sts-clear").addEventListener("click", clearSelection);
  }

  // ── Fetch teams from Netlify function ──────────────────────────────────────
  async function loadTeams(mount) {
    dropdownEl.innerHTML = `<div class="sts-loading">Loading teams…</div>`;

    try {
      const res = await fetch(TEAMS_API);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      allTeams = await res.json();
    } catch (err) {
      console.warn("SYNC Team Selector: could not load teams", err);
      dropdownEl.innerHTML = `<div class="sts-no-results">Could not load team list. Please refresh.</div>`;
    }
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  async function init() {
    const mount = document.getElementById(MOUNT_ID);
    if (!mount) return;

    injectStyles();
    renderShell(mount);
    await loadTeams(mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
